/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import React, { useState, useRef, useEffect } from 'react';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import SvgIcon from 'helpers/SvgIcon/SvgIcon';
import { useCardListContext } from './CardList';
import useDictionary from 'lib/hooks/useDictionary';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { getVideoPlatform, getProcessedVideoUrl } from 'lib/utils/videoUtils';
import { getTestProps } from 'lib/testing/utils';
import { RemixIcon } from 'helpers/RemixIcon';
import TrackedVideoIframe from 'helpers/GenericWrappers/TrackedVideoIframe/TrackedVideoIframe';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
const VideoCardItem = (props)=>{
    const { videoUrl, cardImage, cardLink1, description, eyebrow, heading, subHeading } = props?.fields || {};
    const RenderingIdentifier = props?.rendering?.uid || 'videomodaldefault';
    const { page } = useSitecore();
    // Get column count from CardList context
    const cardListColCount = useCardListContext();
    const renderingParams = props?.params ?? props?.rendering?.params;
    const hasRightVideoParam = renderingParams?.rightVideo !== undefined;
    const isRightVideo = hasRightVideoParam && renderingParams?.rightVideo === '1';
    const effectiveColCount = cardListColCount ?? '1';
    const videoSideClass = !isRightVideo ? '' : effectiveColCount === '1' ? 'md:flex-row-reverse' : effectiveColCount === '2' ? 'lg:flex-row-reverse' : '';
    const videoCornerClass = !isRightVideo ? '' : effectiveColCount === '1' ? 'md:!rounded-l-none md:!rounded-r-component-article-card-image-radius' : effectiveColCount === '2' ? 'lg:!rounded-l-none lg:!rounded-r-component-article-card-image-radius' : '';
    const { getDictionaryValue } = useDictionary();
    const { sitecoreContext } = useSitecoreContext();
    const youTubeBaseURL = sitecoreContext?.siteSettings?.youTubeBaseURL?.value;
    const isEditing = page?.mode?.isEditing;
    // State for video modal
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [triggerElement, setTriggerElement] = useState(null);
    // Refs for focus management
    const closeButtonRef = useRef(null);
    const playButtonOverlayRef = useRef(null);
    /*
   * Video Modal Functions
   */ const handleVideoClick = (e)=>{
        setTriggerElement(e.currentTarget);
        if (videoUrl?.value) {
            setIsVideoModalOpen(true);
        }
    };
    const handleCloseVideoModal = ()=>{
        setIsVideoModalOpen(false);
    };
    // Effect to handle Escape key press for closing the modal and focus trapping
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            if (event.key === 'Escape' && isVideoModalOpen) {
                handleCloseVideoModal();
            }
            // Focus trapping within modal
            if (isVideoModalOpen) {
                const modal = document.getElementById(`video-modal-${RenderingIdentifier}`);
                const focusableElements = modal?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                if (focusableElements && focusableElements.length > 0) {
                    const firstElement = focusableElements[0];
                    const lastElement = focusableElements[focusableElements.length - 1];
                    if (event.key === 'Tab') {
                        if (event.shiftKey) {
                            // Shift + Tab
                            if (document.activeElement === firstElement) {
                                event.preventDefault();
                                lastElement.focus();
                            }
                        } else {
                            // Tab
                            if (document.activeElement === lastElement) {
                                event.preventDefault();
                                firstElement.focus();
                            }
                        }
                    }
                }
            }
        };
        if (isVideoModalOpen) {
            window.addEventListener('keydown', handleKeyDown);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }
        return ()=>{
            window.removeEventListener('keydown', handleKeyDown);
            // Restore body scroll when modal closes
            document.body.style.overflow = 'unset';
        };
    }, [
        isVideoModalOpen,
        RenderingIdentifier
    ]);
    // Effect for focus management
    useEffect(()=>{
        if (isVideoModalOpen) {
            // Focus the modal's close button when it opens
            closeButtonRef.current?.focus();
        } else {
            // Return focus to the trigger element when the modal closes
            triggerElement?.focus();
        }
    }, [
        isVideoModalOpen,
        triggerElement
    ]);
    // Check if video URL is valid
    const isValidVideoUrl = videoUrl?.value && getVideoPlatform(videoUrl.value);
    // Link needs a destination (href or anchor) and text on the live site; always render in Page Builder so it can be authored
    const hasCardLink1 = !!((cardLink1?.value?.href || cardLink1?.value?.anchor) && cardLink1?.value?.text);
    /*
   * Rendering
   */ const { body, contentWrapper, content, descriptionText, eyebrowText, videoImageContainer, headingWithEyebrow, headingText, subheading, container, videoLinkWrapper, videoLink, cta, modalOverlay, modalContent, modalCloseButton, playButtonOverlay, modalTitle, modalSubheading, modalDescription, videoOverlay, playButtonIcon, modalVideoContainer, modalVideoIframe, imageWrapper, sectionDivider } = TAILWIND_VARIANTS({
        colCount: cardListColCount
    });
    return (
        /*#__PURE__*/ _jsxs("div", {
            className: 'h-full',
            ...getTestProps(`component-video-card-item-${props?.rendering?.uid}`),
            children: [
                (()=>{
                    const raw = {
                        '@context': 'https://schema.org',
                        '@type': 'VideoObject',
                        ...(heading?.value ? {
                            name: heading?.value
                        } : {}),
                        ...(description?.value ? {
                            description: description?.value
                        } : {}),
                        ...(cardImage?.value?.src ? {
                            thumbnailUrl: cardImage?.value?.src
                        } : {}),
                        ...(videoUrl?.value ? {
                            contentUrl: videoUrl?.value
                        } : {}),
                        ...(isValidVideoUrl ? {
                            embedUrl: getProcessedVideoUrl(videoUrl?.value || '', youTubeBaseURL)
                        } : {})
                    };
                    const jsonLd = Object.fromEntries(Object.entries(raw).filter(([, value])=>value !== undefined));
                    if (isEditing) return null;
                    return /*#__PURE__*/ _jsx("script", {
                        type: "application/ld+json",
                        dangerouslySetInnerHTML: {
                            __html: JSON.stringify(jsonLd)
                        }
                    });
                })(),
                /*#__PURE__*/ _jsxs("article", {
                    className: `${container()} ${videoSideClass}`,
                    "data-component": "authorable/shared/lists/videocarditem",
                    ...editingHydrationProps(isEditing),
                    id: RenderingIdentifier,
                    children: [
                        /*#__PURE__*/ _jsxs("button", {
                            ref: playButtonOverlayRef,
                            onClick: handleVideoClick,
                            className: `${videoImageContainer()} ${videoCornerClass}`,
                            type: "button",
                            "aria-label": `Play video: ${heading?.value || 'Video'}`,
                            "aria-expanded": isVideoModalOpen,
                            "aria-controls": isVideoModalOpen ? `video-modal-${RenderingIdentifier}` : undefined,
                            "aria-describedby": `video-description-${RenderingIdentifier}`,
                            disabled: !isValidVideoUrl,
                            children: [
                                cardImage?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                                    field: cardImage,
                                    className: `${imageWrapper()} ${videoCornerClass}`,
                                    ...getTestProps(`thumbnail-image`)
                                }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                                    field: {
                                        value: {
                                            src: '/assets/profile_placeholder.png',
                                            alt: 'No image'
                                        }
                                    },
                                    className: `${imageWrapper()} ${videoCornerClass}`,
                                    ...getTestProps(`related-profile-image-placeholder`)
                                }),
                                isValidVideoUrl && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: `${videoOverlay()} ${videoCornerClass}`,
                                            ...getTestProps(`video-overlay`)
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: playButtonOverlay(),
                                            ...getTestProps(`play-button-overlay`),
                                            children: [
                                                /*#__PURE__*/ _jsx(RemixIcon, {
                                                    name: "ri-play-line",
                                                    className: playButtonIcon()
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "sr-only",
                                                    children: "Play video"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: body(),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: contentWrapper(),
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: content(),
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: headingWithEyebrow(),
                                                children: [
                                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                        className: eyebrowText(),
                                                        editable: true,
                                                        field: eyebrow,
                                                        tag: "div",
                                                        ...getTestProps(`eyebrow`)
                                                    }),
                                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                        className: headingText(),
                                                        field: heading,
                                                        tag: "h3",
                                                        ...getTestProps(`video-title`)
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                className: subheading(),
                                                field: subHeading,
                                                tag: "h4",
                                                ...getTestProps(`sub-heading`)
                                            }),
                                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                                className: descriptionText(),
                                                field: description,
                                                id: `video-description-${RenderingIdentifier}`,
                                                ...getTestProps(`description`)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: sectionDivider()
                                            })
                                        ]
                                    }),
                                    (isValidVideoUrl || isEditing || hasCardLink1) && /*#__PURE__*/ _jsxs("div", {
                                        className: videoLinkWrapper(),
                                        children: [
                                            isValidVideoUrl && /*#__PURE__*/ _jsx(ButtonWrapper, {
                                                ctaComponentClass: "default",
                                                ctaVariant: "primary",
                                                ctaIcon: "arrow-right",
                                                className: videoLink(),
                                                text: getDictionaryValue('WatchVideo', 'Watch video'),
                                                type: "button",
                                                "aria-expanded": isVideoModalOpen,
                                                "aria-controls": `video-modal-${RenderingIdentifier}`,
                                                "aria-describedby": `video-description-${RenderingIdentifier}`,
                                                ...getTestProps(`play-cta`),
                                                onClick: handleVideoClick
                                            }),
                                            (isEditing || hasCardLink1) && /*#__PURE__*/ _jsx(LinkWrapper, {
                                                ctaComponentClass: "component-video-card-item-button-1-color",
                                                className: cta(),
                                                ctaVariant: "secondary",
                                                field: cardLink1,
                                                ctaIcon: "arrow-right",
                                                "aria-label": cardLink1?.value?.title || cardLink1?.value?.text ? cardLink1?.value?.title || cardLink1?.value?.text : undefined,
                                                ...getTestProps(`primary-cta`),
                                                gtmEvent: createCTAClickGTMEvent({
                                                    header: heading?.value,
                                                    ctaText: cardLink1?.value?.text,
                                                    ctaType: 'clear',
                                                    componentName: 'Card Set',
                                                    ctaLink: cardLink1?.value?.href
                                                })
                                            })
                                        ]
                                    })
                                ]
                            })
                        })
                    ]
                }),
                isVideoModalOpen && /*#__PURE__*/ _jsx("div", {
                    id: `video-modal-${RenderingIdentifier}`,
                    className: modalOverlay(),
                    onClick: handleCloseVideoModal,
                    role: "dialog",
                    "aria-modal": "true",
                    "aria-labelledby": `modal-title-${RenderingIdentifier}`,
                    "aria-describedby": `modal-description-${RenderingIdentifier}`,
                    ...getTestProps(`video-modal-${RenderingIdentifier}`),
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: modalContent(),
                        onClick: (e)=>e.stopPropagation(),
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                ref: closeButtonRef,
                                onClick: handleCloseVideoModal,
                                className: modalCloseButton(),
                                type: "button",
                                "aria-label": "Close video modal",
                                ...getTestProps(`close-video-modal`),
                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                    icon: "close",
                                    size: "xxs"
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: modalVideoContainer(),
                                children: /*#__PURE__*/ _jsx(TrackedVideoIframe, {
                                    videoUrl: videoUrl?.value || '',
                                    videoTitle: heading?.value?.trim() || 'Video content',
                                    src: getProcessedVideoUrl(videoUrl?.value || '', youTubeBaseURL),
                                    title: heading?.value?.trim() || 'Video content',
                                    "aria-label": heading?.value?.trim() || 'Video content',
                                    className: modalVideoIframe(),
                                    allowFullScreen: true,
                                    allow: "autoplay; fullscreen; picture-in-picture; encrypted-media; presentation",
                                    sandbox: "allow-same-origin allow-scripts allow-presentation",
                                    referrerPolicy: "strict-origin-when-cross-origin",
                                    ...getTestProps(`video-iframe`)
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: modalTitle(),
                                        field: heading,
                                        tag: "h3",
                                        id: `modal-title-${RenderingIdentifier}`,
                                        ...getTestProps(`modal-title`)
                                    }),
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: modalSubheading(),
                                        field: subHeading,
                                        tag: "h4",
                                        ...getTestProps(`modal-sub-heading`)
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                                        className: modalDescription(),
                                        field: description,
                                        id: `modal-description-${RenderingIdentifier}`,
                                        ...getTestProps(`modal-description`)
                                    })
                                ]
                            })
                        ]
                    })
                })
            ]
        })
    );
};
export const Default = withStandardComponentWrapper(VideoCardItem);
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        style: 'primary',
        colCount: '1'
    },
    slots: {
        container: [
            'flex',
            'w-full',
            'overflow-hidden',
            'border-component-article-card-border-width',
            'border-component-video-card-item-border',
            'rounded-general-border-radius-container',
            'bg-component-video-card-item-surface',
            'shadow-color-black-alpha-10',
            'shadow-[0_0_8px_0]'
        ],
        body: [
            'flex',
            'flex-col',
            'h-full',
            'w-full',
            'justify-center',
            'items-center'
        ],
        contentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'px-component-card-item-landscape-content-padding-x',
            'py-component-card-item-landscape-content-padding-y'
        ],
        content: [
            'flex',
            'flex-col',
            'h-full',
            'w-full',
            'gap-spacing-spacing-24'
        ],
        headingWithEyebrow: [
            'flex',
            'flex-col',
            'w-full',
            'gap-spacing-spacing-4'
        ],
        eyebrowText: [
            'text-component-card-item-eyebrow',
            'font-typography-font-family-body',
            'text-typography-font-size-body-xSmall',
            'mb-spacing-spacing-4',
            'font-semibold',
            'font-normal',
            'line-clamp-1',
            'leading-[150%]'
        ],
        videoImageContainer: [
            'relative',
            'min-h-max',
            'w-full',
            'h-full md:h-auto',
            'rounded-component-article-card-image-radius',
            'rounded-b-none md:rounded-component-article-card-image-radius md:rounded-r-none',
            'overflow-hidden',
            'text-color-fill-brand-1',
            'transition',
            'duration-300',
            'ease-in-out',
            'group'
        ],
        headingText: [
            'font-typography-font-family-header',
            'font-normal',
            'text-typography-font-size-medium',
            'text-component-card-item-title',
            'leading-typography-line-height-medium',
            'line-clamp-2'
        ],
        subheading: [
            'font-semibold',
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'leading-[150%]',
            'text-component-card-item-subtitle',
            'line-clamp-1'
        ],
        descriptionText: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-component-card-item-body',
            'leading-typography-line-height-body-medium',
            'mb-spacing-spacing-8',
            'font-typography-font-weight-body'
        ],
        sectionDivider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        videoLinkWrapper: [
            'w-fit',
            'flex',
            'flex-wrap',
            'items-center',
            'justify-start',
            'gap-spacing-spacing-16'
        ],
        videoLink: [
            'px-spacing-spacing-24',
            'py-spacing-spacing-16',
            'rounded-general-border-radius-button',
            'font-typography-font-family-body',
            'font-semibold',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-xxsmall',
            'text-component-button-primary-inverse-text',
            'flex',
            'gap-2',
            'cursor-pointer',
            'hover:opacity-75',
            'transition-opacity',
            'duration-200'
        ],
        cta: [
            'flex',
            'gap-spacing-spacing-8',
            'items-center',
            'justify-center',
            'py-spacing-spacing-16',
            'px-spacing-spacing-24',
            '!w-auto'
        ],
        modalOverlay: [
            'fixed',
            'inset-0',
            'bg-black',
            'bg-opacity-75',
            'flex',
            'items-center',
            'justify-center',
            'z-50',
            'p-4'
        ],
        modalContent: [
            'flex',
            'flex-col',
            'relative',
            'bg-color-surface-white',
            'w-full',
            'rounded-general-border-radius-container',
            'md:p-spacing-spacing-40',
            'px-spacing-spacing-16',
            'py-spacing-spacing-24',
            'max-w-4xl',
            'max-h-[90vh]',
            'gap-4',
            'overflow-y-auto'
        ],
        modalCloseButton: [
            'absolute',
            'right-2',
            'top-2',
            'md:top-4',
            'md:right-8',
            'text-typography-font-size-body-large',
            'font-typography-font-weight-body-bold',
            'text-color-text-text-secondary',
            'hover:text-color-text-text',
            'cursor-pointer',
            'z-10',
            'w-8',
            'h-8',
            'flex',
            'items-center',
            'justify-center'
        ],
        modalTitle: [
            'font-typography-font-weight-body-bold',
            'text-typography-font-size-body-large',
            'text-component-video-card-item-title',
            'line-clamp-2'
        ],
        modalSubheading: [
            'font-typography-font-weight-body-bold',
            'text-typography-font-size-body-large',
            'text-component-video-card-item-subtitle',
            'line-clamp-2'
        ],
        modalDescription: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-component-video-card-item-body',
            'leading-typography-line-height-body-medium',
            'mb-spacing-spacing-8',
            'font-typography-font-weight-body'
        ],
        videoPlayIcon: [
            '!h-5',
            '!w-5',
            'text-component-button-primary-link-icon',
            'transition-all',
            'duration-300'
        ],
        videoOverlay: [
            'absolute',
            'inset-0',
            'bg-black',
            'bg-opacity-0',
            'group-hover:bg-black/30',
            'group-focus:bg-black/30',
            'transition-all',
            'duration-300',
            'ease-in-out',
            'rounded-component-article-card-image-radius'
        ],
        playButtonOverlay: [
            'w-11',
            'h-11',
            'absolute',
            'z-10',
            'bg-color-fill-white hover:bg-color-fill-brand-2 focus::bg-color-fill-brand-2 focus-visible:bg-color-fill-brand-2 focus-within::bg-color-fill-brand-2',
            'rounded-border-radius-radius-10',
            'shadow-color-black-alpha-10',
            'shadow-[0_0_8px_0]',
            'text-color-fill-brand-2 hover:text-color-fill-white',
            'focus:border focus:border-black focus:bg-color-fill-brand-2 focus:text-color-fill-white',
            'focus-visible:border focus-visible:border-black focus-visible:bg-color-fill-brand-2 focus-visible:text-color-fill-white',
            'focus-within:border focus-within:border-black focus-within:bg-color-fill-brand-2 focus-within:text-color-fill-white',
            'p-[6px]'
        ],
        playButtonIcon: [
            'w-8',
            'h-8',
            'transition-all',
            'duration-300'
        ],
        modalVideoContainer: [
            'relative',
            'w-full',
            'shrink-0',
            'aspect-video',
            'mt-spacing-spacing-16',
            'rounded-general-border-radius-label',
            'overflow-hidden'
        ],
        modalVideoIframe: [
            'absolute',
            'top-0',
            'left-0',
            'w-full',
            'h-full',
            'rounded-general-border-radius-label'
        ],
        imageWrapper: [
            'w-full',
            'h-full',
            'object-cover',
            'rounded-component-article-card-image-radius',
            'rounded-b-none md:rounded-component-article-card-image-radius md:rounded-r-none'
        ]
    },
    variants: {
        colCount: {
            '1': {
                container: [
                    'h-full',
                    'flex-col',
                    'md:flex-row',
                    'md:gap-0'
                ],
                videoImageContainer: [
                    'aspect-[1.61/1]'
                ],
                imageWrapper: [
                    'aspect-[1.61/1]'
                ],
                videoOverlay: [
                    'rounded-component-article-card-image-radius rounded-b-none md:rounded-r-none lg:rounded-b-none'
                ],
                body: [
                    'flex-row',
                    'items-start',
                    'text-left',
                    'lg:pl-0'
                ],
                description: [
                    'md:line-clamp-3',
                    'lg:line-clamp-6'
                ],
                contentWrapper: [
                    'text-left',
                    'justify-center',
                    'gap-spacing-spacing-24'
                ],
                playButtonOverlay: [
                    'bottom-3',
                    'right-3',
                    'lg:bottom-5',
                    'lg:right-5',
                    'scale-75',
                    'lg:scale-100'
                ]
            },
            '2': {
                container: [
                    'h-full',
                    'flex-col',
                    'lg:flex-row'
                ],
                videoImageContainer: [
                    'aspect-[1.61/1] md:aspect-[16/9] lg:aspect-[5/4] md:min-h-[304px]',
                    'rounded-r-none',
                    'md:rounded-b-none',
                    'lg:rounded-r-none'
                ],
                imageWrapper: [
                    'rounded-r-none',
                    'md:rounded-b-none',
                    'lg:rounded-r-none',
                    'aspect-[1.61/1] md:aspect-[16/9] lg:aspect-[5/4]'
                ],
                videoOverlay: [],
                body: [
                    'flex-row',
                    'items-start',
                    'text-left',
                    'h-auto',
                    'md:h-full'
                ],
                description: [
                    'md:line-clamp-3',
                    'lg:line-clamp-2'
                ],
                contentWrapper: [
                    'text-left',
                    'justify-center',
                    'h-auto',
                    'md:h-full',
                    'md:min-h-0',
                    'gap-spacing-spacing-24'
                ],
                playButtonOverlay: [
                    'bottom-3',
                    'right-3'
                ]
            },
            '3': {
                container: [
                    'h-full',
                    'flex-col',
                    'md:flex-row',
                    'lg:flex-col'
                ],
                body: [
                    'flex-col',
                    'items-start',
                    'text-left'
                ],
                contentWrapper: [
                    'h-full',
                    'text-left',
                    'gap-spacing-spacing-24'
                ],
                playButtonOverlay: [
                    'bottom-3',
                    'right-3'
                ],
                videoLink: [
                    'w-fit',
                    'h-auto'
                ],
                videoImageContainer: [
                    'rounded-component-article-card-image-radius rounded-b-none md:rounded-r-none lg:rounded-b-none',
                    'aspect-[1.61/1]'
                ],
                imageWrapper: [
                    'rounded-component-article-card-image-radius rounded-b-none md:rounded-r-none lg:rounded-b-none'
                ]
            },
            '4': {
                container: [
                    'h-full',
                    'flex-col'
                ],
                body: [
                    'flex-col',
                    'items-center',
                    'text-left'
                ],
                contentWrapper: [
                    'h-full',
                    'text-left',
                    'gap-spacing-spacing-24'
                ],
                playButtonOverlay: [
                    'bottom-3',
                    'right-3'
                ],
                videoLink: [
                    'w-fit',
                    'h-auto'
                ],
                videoImageContainer: [
                    'aspect-[1.61/1]'
                ],
                imageWrapper: [
                    'aspect-[1.61/1]'
                ]
            }
        }
    }
});
